import * as moment from 'moment';
import { useMemo } from 'react';
import { ColDef, FirstDataRenderedEvent, GridReadyEvent, GridSizeChangedEvent, ICellRendererParams, ValueGetterParams, IHeaderParams, RowDataChangedEvent } from 'ag-grid-community';
import { AgGridReact, } from 'ag-grid-react';
import { GridWrapper } from '../styles';
import { Label } from 'react-bootstrap';
import { IWatrReimbursement } from '@nkr/models/index';
import { ReimbursementType } from '@nkr/enums/index';
import SortableHeaderComponent from '@nkr/views/admin/center/list/common/sortable-header';

interface IProps {
    data: IWatrReimbursement[],
    onGridReady: (e: GridReadyEvent) => void,
    onGridSizeChanged: (e: GridSizeChangedEvent) => void,
    onFirstDataRendered: (e: FirstDataRenderedEvent) => void,
}

const TransactionGrid = (props: IProps) => {
    const onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    const cols: ColDef[] = [
        {
            field: 'rank',
            headerName: 'Rank',
            headerComponentParams: { columnName: 'Rank' },
            headerComponentFramework: SortableHeaderComponent,
            sortable: false,
            width: 20,
            valueGetter: (props: ValueGetterParams) => props.node.rowIndex + 1
        },
        {
            field: 'fullName',
            headerName: 'Payee',
            headerComponentParams: { columnName: 'Payee' },
            headerComponentFramework: SortableHeaderComponent,
            sortable: true,
            width: 100
        },
        {
            field: 'date',
            headerName: 'Date',
            headerComponentParams: { columnName: 'Date' },
            headerComponentFramework: SortableHeaderComponent,
            sortable: true,
            width: 100,
            valueGetter: (props: ValueGetterParams) => moment(props.data.reimbursementDate).isValid() ? moment(props.data.date).format('MM/DD/YYYY') : "",
            cellRendererFramework: (props: ICellRendererParams) => {
                return <Label bsStyle='danger' style={{ fontSize: '100%' }}>{props.value}</Label>
            }
        },
        {
            field: 'donationOrganType',
            headerName: 'Organ Type',
            headerComponentParams: { columnName: 'Organ Type' },
            headerComponentFramework: SortableHeaderComponent,
            width: 100,
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IWatrReimbursement;

                if (data.donationOrganType == 1)
                    return <Label bsStyle='danger' style={{ fontSize: '100%' }}>Liver</Label>
                else
                    return <Label bsStyle='warning' style={{ fontSize: '100%' }}>Kidney</Label>
            }
        },
        {
            field: 'organDonated',
            headerName: 'Organ Donated',
            headerComponentParams: { columnName: 'Organ Donated' },
            headerComponentFramework: SortableHeaderComponent,
            sortable: true,
            width: 100,
            valueGetter: (props: ValueGetterParams) => moment(props.data.organDonated).isValid() ? moment(props.data.organDonated).format('MM/DD/YYYY') : "",
        },
        {
            field: 'reimbursementType',
            headerName: 'Type',
            headerComponentParams: { columnName: 'Reimbursement Type' },
            headerComponentFramework: SortableHeaderComponent,
            width: 100,
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IWatrReimbursement;

                if (data.reimbursementType == ReimbursementType.Wages
                    || data.reimbursementType == ReimbursementType.WagesForComplication
                    || data.reimbursementType == ReimbursementType.CreditReturnLostWageReimbursement)
                    return <Label bsStyle='success' style={{ fontSize: '100%' }}>Wages</Label>
                else if (data.reimbursementType == ReimbursementType.TravelAndLodging || data.reimbursementType == ReimbursementType.TravelAndLodgingForComplication)
                    return <Label bsStyle='info' style={{ fontSize: '100%' }}>Travel</Label>
                else if (data.reimbursementType == ReimbursementType.DppReimbursement)
                    return <Label bsStyle='danger' style={{ fontSize: '100%' }}>DPP</Label>
                else if (data.reimbursementType == ReimbursementType.AdvancedReimbursement)
                    return 'Advanced Reimbursement';
                else if (data.reimbursementType == ReimbursementType.InternationalWorkup)
                    return 'International Workup';
                else if (data.reimbursementType == ReimbursementType.DependentCare)
                    return <Label bsStyle='warning' style={{ fontSize: '100%' }}>Dependent Care</Label>
            }
        },

        {
            field: 'hasComplications',
            headerName: 'hasComplications',
            headerComponentParams: { columnName: 'Complications' },
            headerComponentFramework: SortableHeaderComponent,
            width: 110,
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) => {
                let data = props.data as IWatrReimbursement;
                if (data.reimbursementType == ReimbursementType.TravelAndLodgingForComplication
                    || data.reimbursementType == ReimbursementType.WagesForComplication) {
                    return <span className="glyphicon glyphicon-exclamation-sign" style={{ color: '#d9534f' }}></span>
                }
                return <></>;

            }
        },

        {
            field: 'dependentCareAmount',
            headerName: 'DependentCareAmount',
            headerComponentFramework: (params: IHeaderParams) =>
                <SortableHeaderComponent {...params}>
                    Dependent<br />Care<br /> Amount
                </SortableHeaderComponent>,
            width: 60,
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) =>
                <div style={{ textAlign: 'right' }}>
                    {props.data.dependentCareAmount != undefined && props.data.dependentCareAmount != 0 && props.data.reimbursementType == 2
                        ? "$" + props.data.dependentCareAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : '--'}
                </div>
        },

        {
            field: 'amount',
            headerName: 'Amount',
            headerComponentParams: { columnName: 'Amount' },
            headerComponentFramework: SortableHeaderComponent,
            width: 50,
            sortable: true,
            cellRendererFramework: (props: ICellRendererParams) =>
                <div style={{ textAlign: 'right' }}>
                    ${props.data.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
        },

        {
            field: 'confirmationCode',
            headerName: 'Confirmation #',
            headerComponentParams: { columnName: 'Confirmation #' },
            headerComponentFramework: SortableHeaderComponent,
            sortable: true,
            width: 170
        },
        {
            field: 'comments',
            headerName: 'Comments',
            headerComponentParams: { columnName: 'Comments' },
            headerComponentFramework: SortableHeaderComponent,
            sortable: true,
            hide: true,
            cellRendererFramework: (props: ICellRendererParams) => {
                return <Label bsStyle='danger' style={{ fontSize: '100%' }}>{props.value != undefined
                    ? props.value.replace(' | ', '<br />')
                    : ''}</Label>
            }
        }
    ]

    const columnDefs = useMemo(() => cols, []);
    const rowData = useMemo(() => props.data, [props.data]);

    return (
        <GridWrapper id="grid-wrapper" className='container-fluid-max-sm ag-theme-bootstrap' style={{ height: '62vh' }}>
            <AgGridReact
                columnDefs={columnDefs}
                rowData={rowData}
                headerHeight={52}
                rowHeight={35}
                suppressCellSelection={true}
                rowStyle={{ overflow: 'visible' }}
                noRowsOverlayComponentFramework={() => <div>None found...</div>}
                onGridReady={props.onGridReady}
                onGridSizeChanged={props.onGridSizeChanged}
                onFirstDataRendered={props.onFirstDataRendered}
                onRowDataChanged={onRowDataChanged}
            />
        </GridWrapper>
    );
}

export default TransactionGrid;